/**
 * Formatery wiązane z językiem interfejsu (fala 9, grupy 59 i 89).
 *
 * `formatDate(…, 'long')` wymaga języka — dawniej EN dostawał polski
 * dopełniacz („10 października 2026"). Hook bierze język z i18next, więc ekran
 * nie wybiera go sam. Chwile (znaczniki czasu z API) formatter liczy w strefie
 * Europe/Warsaw, więc `createdAt.slice(0, 10)` — dzień UTC — nie jest potrzebne
 * i nie jest poprawne: wpis z 00:30 w Polsce trafiał na dzień wcześniej.
 */
import { useTranslation } from 'react-i18next'
import {
  vocativePL,
  formatDate,
  formatDecimal,
  formatHours,
  type DateVariant,
  type FormatDecimalOptions,
  type FormatLanguage,
} from '@k1p4/i18n'

/** Wariant miesiąca: „Wrzesień 2026", „wrz" albo „09/2026" (fala 9, grupa 151). */
export type MonthVariant = 'long' | 'short' | 'period'

/** Język formatera z języka i18next — wszystko poza angielskim to polski. */
export function formatLanguageOf(language: string | undefined): FormatLanguage {
  return language?.startsWith('en') ? 'en' : 'pl'
}

export interface Formatters {
  readonly lng: FormatLanguage
  /** Data kalendarzowa albo chwila — dzień chwili w Polsce. */
  date(value: Date | string, variant: DateVariant): string
  /** Liczba dziesiętna w zapisie języka — „0,5", „1 234,5". */
  decimal(value: number | string, options?: FormatDecimalOptions): string
  /** Godziny — „29,5 h". */
  hours(value: number | string): string
  /**
   * Miesiąc „rrrr-mm" (albo „rrrr-mm-dd") słownie. Nazwy bierzemy z zasobów
   * (`hrPayroll.months`) — jedno źródło z nazwą okresu listy płac; surowe ISO
   * zostaje tylko w plikach maszynowych (ADR-0011).
   */
  month(value: string, variant: MonthVariant): string
}

export function useFormat(): Formatters {
  const { t, i18n } = useTranslation()
  const lng = formatLanguageOf(i18n.resolvedLanguage ?? i18n.language)
  return {
    lng,
    date: (value, variant) =>
      variant === 'long' ? formatDate(value, 'long', lng) : formatDate(value, variant, lng),
    decimal: (value, options) => formatDecimal(value, lng, options),
    hours: (value) => formatHours(value, lng),
    month: (value, variant) => {
      const match = /^(\d{4})-(\d{2})/.exec(value)
      const index = match ? Number(match[2]) : 0
      if (!match || index < 1 || index > 12) {
        throw new RangeError(`month: nieprawidłowy miesiąc „${value}" (oczekiwano rrrr-mm)`)
      }
      const year = match[1]
      if (variant === 'period') return `${match[2]}/${year}`
      if (variant === 'short') return t(`hrPayroll.monthsShort.${index}`)
      return `${t(`hrPayroll.months.${index}`)}\u00a0${year}`
    },
  }
}

/**
 * Imię do powitania (fala 9, grupa 162): po polsku wołacz („Cześć, Marto”),
 * a imię spoza słownika daje `null` — ekran wybiera wtedy powitanie neutralne
 * zamiast mianownika. Po angielsku imię bez zmian.
 */
export function greetingName(lng: FormatLanguage, firstName: string | null | undefined): string | null {
  const name = firstName?.trim() ?? ''
  if (name === '') return null
  return lng === 'pl' ? vocativePL(name) : name
}
